import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

interface ToggleProps {
	checked?: boolean;
	onChange?: (checked: boolean) => void;
	disabled?: boolean;
	className?: string;
	label?: string;
	id?: string;
}

export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
	(
		{ checked = false, onChange, disabled = false, className, label, id },
		ref,
	) => {
		const colors = useThemeColors();
		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			onChange?.(e.target.checked);
		};

		const handleKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>) => {
			if (e.key === ' ' && !disabled) {
				e.preventDefault();
				onChange?.(!checked);
			}
		};

		return (
			<label
				htmlFor={id}
				className={cn(
					'relative inline-flex cursor-pointer items-center',
					disabled && 'cursor-not-allowed opacity-50',
					className,
				)}
				onKeyDown={handleKeyDown}
				tabIndex={disabled ? -1 : 0}
				role="switch"
				aria-checked={checked}
				aria-disabled={disabled}
			>
				<input
					ref={ref}
					id={id}
					type="checkbox"
					className="hidden"
					checked={checked}
					onChange={handleChange}
					disabled={disabled}
				/>
				<div
					onClick={(e) => {
						e.preventDefault();
						if (!disabled) {
							onChange?.(!checked);
						}
					}}
					className={cn(
						'h-6 w-11 rounded-full transition-colors duration-200 ease-in-out',
						checked ? 'bg-accent' : 'bg-control',
					)}
				>
					<div
						className={cn(
							'h-5 w-5 transform rounded-full bg-surface shadow-lg transition-transform duration-200 ease-in-out',
							'ring-0',
							checked ? 'translate-x-5' : 'translate-x-0.5',
							'mt-0.5',
						)}
					/>
				</div>
				{label && (
					<span
						className={cn(
							'ml-3 text-sm font-medium',
							colors.classes.textPrimary,
						)}
					>
						{label}
					</span>
				)}
			</label>
		);
	},
);

Toggle.displayName = 'Toggle';
